import React, { useEffect, useId, useState } from 'react';

// ─── Field ──────────────────────────────────────────────────────────────────

export const Field: React.FC<{
  label: React.ReactNode;
  hint?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ label, hint, children, className = '' }) => (
  <label className={`cwf-field ${className}`.trim()}>
    <span className="cwf-field__label">{label}</span>
    {children}
    {hint ? <span className="cwf-field__hint">{hint}</span> : null}
  </label>
);

/**
 * Same shape as `Field` but not a `<label>`.
 *
 * A label owns exactly one control and forwards clicks to it, so wrapping a group of
 * buttons or checkboxes in one swallows their clicks and drops them out of the
 * accessibility tree — the control is there in the DOM but unreachable by role.
 * Use this whenever the content is more than a single input.
 */
export const FieldGroup: React.FC<{
  label: React.ReactNode;
  hint?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ label, hint, children, className = '' }) => (
  <div className={`cwf-field ${className}`.trim()}>
    <span className="cwf-field__label">{label}</span>
    {children}
    {hint ? <span className="cwf-field__hint">{hint}</span> : null}
  </div>
);

// ─── Segmented choice ───────────────────────────────────────────────────────

/**
 * Выбор одного из нескольких вариантов кнопками, как в запуске Config Tuning.
 * Раскрывать вместо `select` стоит там, где вариантов немного и человек должен
 * видеть их все сразу — например подход к оптимизации.
 */
export const SegmentGroup: React.FC<{
  label: React.ReactNode;
  hint?: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, hint, children }) => {
  const id = useId();
  return (
    <div className="cwf-field">
      <span className="cwf-field__label" id={id}>
        {label}
      </span>
      <div className="cwt-seg" role="group" aria-labelledby={id}>
        {children}
      </div>
      {hint ? <span className="cwf-field__hint">{hint}</span> : null}
    </div>
  );
};

export const SegmentButton: React.FC<{
  active: boolean;
  onClick: () => void;
  title?: string;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ active, onClick, title, disabled, children }) => (
  <button
    type="button"
    className={`cwt-seg__btn ${active ? 'cwt-seg__btn--active' : ''}`}
    aria-pressed={active}
    title={title}
    disabled={disabled}
    onClick={onClick}
  >
    {children}
  </button>
);

// ─── Collapsible section ────────────────────────────────────────────────────

export const Section: React.FC<{
  title: string;
  summary?: React.ReactNode;
  badge?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, summary, badge, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`cwf-section ${open ? 'cwf-section--open' : ''}`}>
      <button type="button" className="cwf-section__head" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <i className={`fas ${open ? 'fa-chevron-down' : 'fa-chevron-right'}`} aria-hidden="true" />
        <span className="cwf-section__title">{title}</span>
        {badge}
        {!open && summary != null ? <span className="cwf-section__summary">{summary}</span> : null}
      </button>
      {open ? <div className="cwf-section__body">{children}</div> : null}
    </div>
  );
};

// ─── Chips input (list of short strings) ────────────────────────────────────

export const ChipsInput: React.FC<{
  value: string[];
  onChange: (next: string[]) => void;
  suggestions?: string[];
  placeholder?: string;
  emptyText?: string;
  disabled?: boolean;
}> = ({ value, onChange, suggestions = [], placeholder = 'add…', emptyText = 'empty', disabled }) => {
  const [draft, setDraft] = useState('');
  const listId = useId();

  const add = () => {
    const v = draft.trim();
    if (!v || value.indexOf(v) !== -1) {
      setDraft('');
      return;
    }
    onChange([...value, v]);
    setDraft('');
  };

  return (
    <div className="cwf-chips">
      {value.map((item) => (
        <span key={item} className="cwf-chip">
          {item}
          {!disabled ? (
            <button
              type="button"
              className="cwf-chip__remove"
              title={`Remove ${item}`}
              onClick={() => onChange(value.filter((x) => x !== item))}
            >
              ×
            </button>
          ) : null}
        </span>
      ))}
      {value.length === 0 && disabled ? <span className="cwf-chips__empty">{emptyText}</span> : null}
      {!disabled ? (
        <span className="cwf-chips__add">
          <input
            className="cwf-input cwf-input--sm"
            list={listId}
            placeholder={placeholder}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
          />
          <datalist id={listId}>
            {suggestions.filter((s) => value.indexOf(s) === -1).map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
          <button type="button" className="cwf-btn cwf-btn--xs" disabled={!draft.trim()} onClick={add}>
            <i className="fas fa-plus" aria-hidden="true" />
          </button>
        </span>
      ) : null}
    </div>
  );
};

// ─── Schema-free structured fields ──────────────────────────────────────────

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const StructuredArray: React.FC<{
  value: unknown[];
  onChange: (next: unknown[]) => void;
  disabled?: boolean;
  depth: number;
}> = ({ value, onChange, disabled, depth }) => {
  const blankItem = () => {
    const sample = value[0];
    if (isPlainRecord(sample)) return {};
    if (Array.isArray(sample)) return [];
    if (typeof sample === 'number') return 0;
    if (typeof sample === 'boolean') return false;
    return '';
  };
  return (
    <div className="cwf-structured-array">
      {value.length === 0 ? <span className="cwf-structured__empty">No items configured</span> : null}
      {value.map((item, index) => (
        <div className="cwf-structured-array__item" key={index}>
          <span className="cwf-structured-array__index">{index + 1}</span>
          <div className="cwf-structured__value">
            <StructuredValue
              value={item}
              onChange={(next) => onChange(value.map((current, itemIndex) => (itemIndex === index ? next : current)))}
              disabled={disabled}
              depth={depth + 1}
            />
          </div>
          {!disabled ? (
            <button
              type="button"
              className="cwf-structured__remove"
              onClick={() => onChange(value.filter((_, itemIndex) => itemIndex !== index))}
              aria-label={`Remove item ${index + 1}`}
            >
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      ))}
      {!disabled ? (
        <button type="button" className="cwf-btn cwf-btn--xs cwf-structured-array__add" onClick={() => onChange([...value, blankItem()])}>
          <i className="fas fa-plus" aria-hidden="true" /> Add item
        </button>
      ) : null}
    </div>
  );
};

const StructuredValue: React.FC<{
  value: unknown;
  onChange: (next: unknown) => void;
  disabled?: boolean;
  depth: number;
}> = ({ value, onChange, disabled, depth }) => {
  if (typeof value === 'boolean') {
    return <label className="cwf-structured__toggle"><input type="checkbox" checked={value} disabled={disabled} onChange={(e) => onChange(e.target.checked)} /><span>{value ? 'Enabled' : 'Disabled'}</span></label>;
  }
  if (typeof value === 'number') {
    return <input className="cwf-input" type="number" value={value} disabled={disabled} onChange={(e) => onChange(Number(e.target.value))} />;
  }
  if (typeof value === 'string') {
    return value.length > 90 || value.includes('\n')
      ? <textarea className="cwf-textarea" rows={3} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
      : <input className="cwf-input" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
  }
  if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
    return <ChipsInput value={value as string[]} disabled={disabled} onChange={onChange} placeholder="Add value" />;
  }
  if (Array.isArray(value)) {
    return <StructuredArray value={value} onChange={onChange} disabled={disabled} depth={depth} />;
  }
  if (isPlainRecord(value)) {
    return <StructuredObject value={value} onChange={onChange} disabled={disabled} depth={depth + 1} />;
  }
  if (value === null || value === undefined) {
    return disabled ? (
      <span className="cwf-structured__empty">Not set</span>
    ) : (
      <select
        className="cwf-input cwf-input--sm"
        value=""
        aria-label="Choose value type"
        onChange={(event) => {
          const initial: Record<string, unknown> = { text: '', number: 0, boolean: false, group: {}, list: [] };
          if (event.target.value) onChange(initial[event.target.value]);
        }}
      >
        <option value="">Not set — choose a value type…</option>
        <option value="text">Text</option>
        <option value="number">Number</option>
        <option value="boolean">Yes / no</option>
        <option value="group">Field group</option>
        <option value="list">List</option>
      </select>
    );
  }
  return <input className="cwf-input" value={String(value)} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
};

const StructuredObject: React.FC<{
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  disabled?: boolean;
  depth?: number;
}> = ({ value, onChange, disabled, depth = 0 }) => {
  const [newKey, setNewKey] = useState('');
  const [newType, setNewType] = useState('text');
  const entries = Object.entries(value);
  const patch = (key: string, next: unknown) => onChange({ ...value, [key]: next });
  const remove = (key: string) => {
    const next = { ...value };
    delete next[key];
    onChange(next);
  };
  const add = () => {
    const key = newKey.trim();
    if (!key || Object.prototype.hasOwnProperty.call(value, key)) return;
    const initial: Record<string, unknown> = { text: '', number: 0, boolean: false, group: {}, list: [] };
    onChange({ ...value, [key]: initial[newType] });
    setNewKey('');
  };
  return (
    <div className={`cwf-structured ${depth ? 'cwf-structured--nested' : ''}`}>
      {entries.length === 0 ? <span className="cwf-structured__empty">No fields configured</span> : entries.map(([key, item]) => (
        <div className="cwf-structured__row" key={key}>
          <div className="cwf-structured__key" title={key}>{key.replace(/[_-]+/g, ' ')}</div>
          <div className="cwf-structured__value"><StructuredValue value={item} onChange={(next) => patch(key, next)} disabled={disabled} depth={depth} /></div>
          {!disabled ? <button type="button" className="cwf-structured__remove" onClick={() => remove(key)} title={`Remove ${key}`} aria-label={`Remove ${key}`}><i className="fas fa-times" aria-hidden="true" /></button> : null}
        </div>
      ))}
      {!disabled ? (
        <div className="cwf-structured__add">
          <input
            className="cwf-input cwf-input--sm"
            value={newKey}
            placeholder="New field name"
            onChange={(e) => setNewKey(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
          />
          <select className="cwf-input cwf-input--sm cwf-structured__type" value={newType} onChange={(e) => setNewType(e.target.value)} aria-label="New field type">
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="boolean">Yes / no</option>
            <option value="group">Group</option>
            <option value="list">List</option>
          </select>
          <button type="button" className="cwf-btn cwf-btn--xs" onClick={add} disabled={!newKey.trim()}>
            <i className="fas fa-plus" aria-hidden="true" /> Add field
          </button>
        </div>
      ) : null}
    </div>
  );
};

/** Human-friendly editor for schema-free backend objects, including nested arrays and objects. */
export const StructuredField: React.FC<{
  label: React.ReactNode;
  value: unknown;
  onChange: (next: unknown) => void;
  hint?: React.ReactNode;
  disabled?: boolean;
}> = ({ label, value, onChange, hint, disabled }) => {
  return (
    <div className="cwf-field">
      <span className="cwf-field__label">{label}</span>
      {isPlainRecord(value) || value == null ? (
        <StructuredObject
          value={isPlainRecord(value) ? value : {}}
          onChange={(next) => onChange(Object.keys(next).length ? next : undefined)}
          disabled={disabled}
        />
      ) : (
        <div className="cwf-structured">
          <StructuredValue value={value} onChange={onChange} disabled={disabled} depth={0} />
        </div>
      )}
      {hint ? <span className="cwf-field__hint">{hint}</span> : null}
    </div>
  );
};

// ─── Lines textarea (string[]) ──────────────────────────────────────────────

export const LinesField: React.FC<{
  label: React.ReactNode;
  value: string[];
  onChange: (next: string[]) => void;
  hint?: React.ReactNode;
  rows?: number;
  placeholder?: string;
}> = ({ label, value, onChange, hint, rows = 3, placeholder }) => {
  const joined = value.join('\n');
  const [text, setText] = useState(joined);
  const [lastExternal, setLastExternal] = useState(joined);

  useEffect(() => {
    if (joined !== lastExternal) {
      setLastExternal(joined);
      setText(joined);
    }
  }, [joined, lastExternal]);

  return (
    <Field label={label} hint={hint ?? 'One item per line.'}>
      <textarea
        className="cwf-textarea"
        rows={rows}
        value={text}
        placeholder={placeholder}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          const next = text
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean);
          setLastExternal(next.join('\n'));
          onChange(next);
        }}
      />
    </Field>
  );
};

// ─── Small helpers ──────────────────────────────────────────────────────────

export const Pill: React.FC<{ tone?: 'neutral' | 'accent' | 'warn' | 'danger' | 'ok'; title?: string; children: React.ReactNode }> = ({
  tone = 'neutral',
  title,
  children,
}) => (
  <span className={`cwf-pill cwf-pill--${tone}`} title={title}>
    {children}
  </span>
);

export const Banner: React.FC<{
  tone: 'error' | 'warn' | 'info' | 'ok';
  title?: React.ReactNode;
  details?: string[];
  actions?: React.ReactNode;
  onDismiss?: () => void;
  children?: React.ReactNode;
}> = ({ tone, title, details, actions, onDismiss, children }) => (
  <div className={`cwf-banner cwf-banner--${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
    <div className="cwf-banner__icon">
      <i
        className={`fas ${
          tone === 'error' ? 'fa-exclamation-circle' : tone === 'warn' ? 'fa-exclamation-triangle' : tone === 'ok' ? 'fa-check-circle' : 'fa-info-circle'
        }`}
        aria-hidden="true"
      />
    </div>
    <div className="cwf-banner__body">
      {title ? <div className="cwf-banner__title">{title}</div> : null}
      {children}
      {details && details.length ? (
        <ul className="cwf-banner__list">
          {details.map((d, i) => (
            <li key={`${i}-${d}`}>{d}</li>
          ))}
        </ul>
      ) : null}
      {actions ? <div className="cwf-banner__actions">{actions}</div> : null}
    </div>
    {onDismiss ? (
      <button type="button" className="cwf-banner__close" onClick={onDismiss} aria-label="Dismiss">
        ×
      </button>
    ) : null}
  </div>
);
